import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, Select, Table, Tag, Typography } from 'antd';
import callApi from '../../Api/config';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import { TabContent } from '../../utils/forms/FormInterface';
import B2BWithdrawDetail from './Detail';
import DefaultTableHeader from '../Entity/AbstractLanding/DefaultTableHeader';
import '../B2BAffiliateProgram/index.scss';

const { Text } = Typography;

type WithdrawRequest = {
  id: string;
  withdrawId: string;
  partnerId: string;
  partnerName: string;
  companyName: string;
  email: string;
  withdrawAmount: number;
  status: 'pending' | 'approved' | 'processed' | 'rejected';
  paymentMethod?: string;
  processingDate?: string;
  processedBy?: string;
  createdAt: string;
};

const statusColors: Record<string, string> = {
  pending: 'orange',
  approved: 'blue',
  processed: 'green',
  rejected: 'red',
};

const B2BWithdraw: React.FC = () => {
  const [requests, setRequests] = useState<WithdrawRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<any>({});
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  const [activeKey, setActiveKey] = useState<string>('0');
  const [detailTabs, setDetailTabs] = useState<TabContent>([]);

  const fetchData = useCallback(
    async (q = search, fObj = filters, p = page) => {
      setLoading(true);
      try {
        const { status, startDate, endDate } = fObj;
        const params: Record<string, any> = {
          page: p,
          limit: pageSize,
          search: q || undefined,
          status: status || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        };
        const res = await callApi('/affiliate/withdraw', 'GET', null, params);
        if (res?.data?.isSuccess) {
          setRequests(res.data?.data?.results ?? []);
          setTotal(res.data?.data?.totalCount ?? 0);
        }
      } finally {
        setLoading(false);
      }
    },
    [search, filters, page]
  );

  useEffect(() => {
    fetchData();
  }, [search, filters, page]);

  const resetFilters = () => {
    setSearch('');
    setFilters({});
    setPage(1);
  };

  const closeTab = (key: string) => {
    setDetailTabs(prev => prev.filter(t => String(t.key) !== key));
    setActiveKey('0');
  };

  const openDetailTab = (row: WithdrawRequest) => {
    const key = row.id;
    if (detailTabs.some(t => String(t.key) === String(key))) {
      setActiveKey(String(key));
      return;
    }
    setDetailTabs(prev => [
      ...prev,
      {
        key,
        tabTitle: row.withdrawId || row.id,
        content: (
          <GenericAccordian key={key}>
            <B2BWithdrawDetail
              requestId={row.id}
              onClose={() => closeTab(key)}
              onSaved={() => fetchData(search, filters, page)}
            />
          </GenericAccordian>
        ),
      },
    ]);
    setActiveKey(String(key));
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action !== 'remove') return;
    const k = targetKey != null ? String(targetKey) : '';
    if (k && k !== '0') {
      closeTab(k);
    }
  };

  const columns = [
    {
      title: 'Withdraw ID',
      dataIndex: 'withdrawId',

      key: 'withdrawId',
      render: (value: string, row: WithdrawRequest) => (
        <Button
          type="link"
          style={{
            padding: 0,
            fontWeight: 600,
            lineHeight: '18px',
            fontSize: 15,
            color: 'black',
          }}
          onClick={e => {
            e.stopPropagation();
            openDetailTab(row);
          }}
        >
          {value}
        </Button>
      ),
    },
    {
      title: 'Partner ID',
      dataIndex: 'partnerId',
      key: 'partnerId',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: 'Partner Name',
      dataIndex: 'partnerName',
      key: 'partnerName',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    { title: 'Company Name', dataIndex: 'companyName', key: 'companyName' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Withdraw Amount',
      dataIndex: 'withdrawAmount',
      key: 'withdrawAmount',
      render: (v: number) => (
        <Text strong style={{ color: '#16a34a' }}>
          ${Number(v).toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => (
        <Tag color={statusColors[v] ?? 'default'} style={{ textTransform: 'capitalize' }}>
          {v}
        </Tag>
      ),
    },
    {
      title: 'Withdraw Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => (v ? new Date(v).toLocaleDateString() : '--'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, row: WithdrawRequest) => (
        <Button
          type="link"
          onClick={e => {
            e.stopPropagation();
            openDetailTab(row);
          }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="B2B Withdraw Requests"
      updateFormVisibility={updateFormVisibility}
      onTabClick={(key: string) => setActiveKey(key)}
      content={detailTabs}
    >
      <div
        className="landing-container"
        style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '20px 24px' }}
      >
        <DefaultTableHeader
          headerName="B2B Withdraw Requests"
          totalCount={total}
          countText="Requests"
          setSearchQuery={setSearch}
          setPhoneSearchQuery={() => {}}
          entityName="/affiliate/withdraw"
          showFilter={true}
          filters={filters}
          filterQuery={setFilters}
          disableAdd={true}
        />

        <Table
          rowKey="id"
          columns={columns}
          dataSource={requests}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: p => {
              setPage(p);
              fetchData(search, filters, p);
            },
            showSizeChanger: false,
            showTotal: (t, r) => `Showing ${r[0]} to ${r[1]} of ${t} requests`,
          }}
          scroll={{ x: 'max-content' }}
          style={{ minHeight: '100vh' }}
          bordered
          onRow={row => ({
            onClick: () => openDetailTab(row),
            style: { cursor: 'pointer' },
          })}
        />
      </div>
    </AbstractTabs>
  );
};

export default B2BWithdraw;
